import { type FreightLocation, type FreightLocationKind, api } from '@/api/client'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/cn'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  value: string
  onChange: (code: string) => void
  kind?: FreightLocationKind | 'all'
  disabled?: boolean
}

export function PortPicker({ value, onChange, kind = 'sea', disabled }: Props) {
  const catalogKind = kind === 'all' ? undefined : kind
  const { t } = useTranslation('freight')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 200)
    return () => window.clearTimeout(id)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const selected = useQuery({
    queryKey: ['freight', 'location', kind, value],
    queryFn: () => api.freight.locations({ q: value, kind: catalogKind }),
    enabled: value.length > 0,
    select: (rows) => rows.find((row) => row.code === value) ?? null,
    staleTime: 60_000,
  })

  const search = useQuery({
    queryKey: ['freight', 'locations', kind, debounced],
    queryFn: () => api.freight.locations({ q: debounced, kind: catalogKind }),
    enabled: open,
    staleTime: 30_000,
  })

  const results = search.data ?? []
  const label = selected.data ? `${selected.data.name}` : value

  return (
    <div ref={rootRef} className={cn('relative')}>
      {value && !open ? (
        <div className="flex min-h-[44px] items-center gap-2 rounded-xl border border-border bg-background px-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{label}</div>
            <div className="font-mono text-[11px] text-muted-foreground">{value}</div>
          </div>
          <button
            type="button"
            disabled={disabled}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
            aria-label={t('ports.clear')}
            onClick={() => {
              onChange('')
              setQuery('')
              setOpen(true)
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            disabled={disabled}
            placeholder={t('ports.search')}
            className="pl-9"
            onChange={(e) => {
              setQuery(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
          />
        </div>
      )}
      {open ? (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-border bg-card shadow-card">
          {search.isLoading ? (
            <li className="px-3 py-3 text-xs text-muted-foreground">{t('ports.loading')}</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-3 text-xs text-muted-foreground">{t('ports.empty')}</li>
          ) : (
            results.map((port) => (
              <li key={port.code}>
                <PortRow
                  port={port}
                  onPick={() => {
                    onChange(port.code)
                    setQuery('')
                    setOpen(false)
                  }}
                />
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}

function PortRow({ port, onPick }: { port: FreightLocation; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex min-h-[44px] w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted"
    >
      <span className="truncate text-sm">{port.name}</span>
      <span className="font-mono text-xs text-muted-foreground">{port.code}</span>
    </button>
  )
}
